import type {
  GetCosmosRegistryChainRequest,
  GetCosmosRegistryChainResponse,
  GetEvmRegistryChainRequest,
  GetEvmRegistryChainResponse,
  GetSupportedChainsResponse,
} from '@polaris/core/models'
import {
  ChainType,
  type GetChainFeeTokenDenomProps,
} from '@polaris/core/models'
import { getQueryClient } from '../configs/tanstack-query'
import { cosmosRegistryChainQuery } from '../queries/keys/chains'
import { apiClient } from './client'

export const getChains = (): Promise<GetSupportedChainsResponse> =>
  apiClient
    .get('supported-chains-sorted', {
      // Server side caching
      next: {
        // https://nextjs.org/docs/app/building-your-application/caching
        // https://nextjs.org/docs/app/api-reference/functions/fetch#optionsnextrevalidate
        revalidate: 60, // 1 minute
      },
    })
    .json()

/**
 * NOTE: api routes do not work when SSR as the URL prefix can't be resolved on server
 * as it can on browser as origin. If you need this data on server, you must extract
 * the API route logic into a function then import that function into server component.
 */
export const getCosmosRegistryChain = (
  request: GetCosmosRegistryChainRequest,
): Promise<GetCosmosRegistryChainResponse> =>
  apiClient
    .get(`/api/chains/cosmos/${request.chainId}`, {
      // Fetching from origin
      prefixUrl: undefined,
      // Server side caching
      next: {
        // https://nextjs.org/docs/app/building-your-application/caching
        // https://nextjs.org/docs/app/api-reference/functions/fetch#optionsnextrevalidate
        revalidate: 60, // 1 minute
      },
    })
    .json()

/**
 * NOTE: api routes do not work when SSR as the URL prefix can't be resolved on server
 * as it can on browser as origin. If you need this data on server, you must extract
 * the API route logic into a function then import that function into server component.
 */
export const getEvmRegistryChain = (
  request: GetEvmRegistryChainRequest,
): Promise<GetEvmRegistryChainResponse> =>
  apiClient
    .get(`/api/chains/evm/${request.id}`, {
      // Fetching from origin
      prefixUrl: undefined,
      // Server side caching
      next: {
        // https://nextjs.org/docs/app/building-your-application/caching
        // https://nextjs.org/docs/app/api-reference/functions/fetch#optionsnextrevalidate
        revalidate: 60, // 1 minute
      },
    })
    .json()

export const getChainFeeTokenDenom = async ({
  chainId,
  chainType,
}: GetChainFeeTokenDenomProps) => {
  if (!chainId || !chainType) {
    return undefined
  }

  switch (chainType) {
    case ChainType.Cosmos: {
      const { chain } = await getQueryClient().ensureQueryData(
        cosmosRegistryChainQuery({
          chainId: chainId.split(':').pop()!,
        }),
      )

      return chain.fees?.fee_tokens?.[0]?.denom
    }

    case ChainType.Evm: {
      // Special case for HyperCore - native gas token is HYPE (denom "150")
      if (chainId === 'evm:hyperliquid') {
        return '150'
      }
      return 'evm-native'
    }

    case ChainType.Solana: {
      return 'solana-native'
    }

    case ChainType.Bitcoin: {
      return 'utxo-native'
    }

    case ChainType.Namada: {
      return 'tnam1q9gr66cvu4hrzm0sd5kmlnjje82gs3xlfg3v6nu7'
    }

    default: {
      console.warn(
        '[getChainFeeTokenDenom]: No valid fee token found. chainId: ',
        chainId,
        ' chainType:',
        chainType,
      )

      return undefined
    }
  }
}
